import { Box, Button, Grid, Group, Text, TextInput } from '@mantine/core';
import { IconCopy, IconDeviceFloppy, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import LockableButton from '../Lockable/LockableButton';
import ConfirmationModal from '../Modals/ConfirmationModal';
import Rules from '../Rules/Rules';

import behavioralRuleFilters from '../../constants/Rules/behaviorRuleFilters';
import behavioralRules from '../../constants/Rules/behaviorSegmentRules';

import catalogRuleFilters from '../../constants/Rules/catalogRuleFilters';
import catalogRules from '../../constants/Rules/catalogRules';

const CatalogTriggerForm = ({ catalogTrigger: passedCatalogTrigger, saveCatalogTrigger, deleteCatalogTrigger }) => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [catalogTrigger, setCatalogTrigger] = useState(passedCatalogTrigger);
  const [customFields, setCustomFields] = useState();
  const [campaigns, setCampaigns] = useState();
  const [webhooks, setWebhooks] = useState();
  const [emails, setEmails] = useState();
  const [eventTags, setEventTags] = useState();
  const [eventActions, setEventActions] = useState();
  const [textMessages, setTextMessages] = useState();
  const [viberMessages, setViberMessages] = useState();
  const [pushNotifications, setPushNotifications] = useState();
  const [subscriptions, setSubscriptions] = useState();
  const [pages, setPages] = useState();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState();
  const [deleteError, setDeleteError] = useState();
  const [segments, setSegments] = useState();
  const [whatsappMessages, setWhatsappMessages] = useState();
  const [surveys, setSurveys] = useState();

  const { accountId, domainId } = useParams();

  // We are filtering out the "At least one of..." rule because it is not used
  // and because it affects the query structure - inner_hits is missing
  const availableBehavioralRules = behavioralRules.filter((rule) => rule.type !== 'or');
  const availableCatalogRules = catalogRules.filter((rule) => rule.type !== 'or');

  const saveDisabled = () => {
    if (!catalogTrigger.name || catalogTrigger.audienceRules.length === 0 || catalogTrigger.catalogRules.length === 0) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
      .then(({ customFields }) => setCustomFields(customFields))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/emails`)
      .then(({ emails }) => setEmails(emails))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pushNotifications`)
      .then(({ pushNotifications }) => setPushNotifications(pushNotifications))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/textMessages`)
      .then(({ textMessages }) => setTextMessages(textMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/viberMessages`)
      .then(({ viberMessages }) => setViberMessages(viberMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns`)
      .then(({ campaigns }) => setCampaigns(campaigns))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventTags`)
      .then(({ eventTags }) => setEventTags(eventTags))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/pages`)
      .then(({ pages }) => setPages(pages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/subscriptions`)
      .then(({ subscriptions }) => setSubscriptions(subscriptions))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/segments`)
      .then(({ segments }) => setSegments(segments))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions?allowedEventsForTrigger=true`)
      .then(({ eventActions }) => {
        setEventActions(eventActions);
      })
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/whatsappMessages`)
      .then(({ whatsappMessages }) => setWhatsappMessages(whatsappMessages))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/webhooks`)
      .then(({ webhooks }) => setWebhooks(webhooks))
      .catch(setError);

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys`)
      .then(({ surveys }) => setSurveys(surveys))
      .catch(setError);
  }, [accountId, domainId, fetch]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  const deleteButton = deleteCatalogTrigger ? (
    <LockableButton restrictionKey="catalogTriggersDelete" color="red" onClick={() => setConfirmDeleteOpen(true)}>
      <IconTrash size={14} /> Delete
    </LockableButton>
  ) : undefined;

  const duplicateButton = catalogTrigger.id ? (
    <LockableButton
      restrictionKey="catalogTriggersCreate"
      color="cyan"
      onClick={() => {
        navigate(
          location.pathname.replace(
            `/catalogTriggers/${catalogTrigger.id}`,
            `/catalogTriggers/new?from=${catalogTrigger.id}`
          )
        );
      }}
    >
      <IconCopy size={14} /> Make a copy
    </LockableButton>
  ) : undefined;

  return (
    <Grid.Col span={{ base: 12, md: 12 }}>
      <Box component="form" ta="left">
        <Grid>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <Text size="sm" fw={500}>
              Name*
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <TextInput
              name="name"
              type="text"
              value={catalogTrigger.name}
              placeholder="e.g. Idle Customers"
              onChange={(e) => setCatalogTrigger({ ...catalogTrigger, [e.target.name]: e.target.value })}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 3 }}>Catalog Rules</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <Rules
              rules={catalogTrigger.catalogRules}
              campaigns={campaigns}
              webhooks={webhooks}
              emails={emails}
              updateRules={(catalogRules) => setCatalogTrigger({ ...catalogTrigger, catalogRules })}
              availableCustomFields={customFields}
              eventTags={eventTags}
              eventActions={eventActions}
              subscriptions={subscriptions}
              pushNotifications={pushNotifications}
              textMessages={textMessages}
              viberMessages={viberMessages}
              pages={pages}
              availableRules={availableCatalogRules}
              availableFilters={catalogRuleFilters}
              flattenProductCustomFields={true}
              segments={segments}
              whatsappMessages={whatsappMessages}
              surveys={surveys}
              invertible={false}
            />
            <Text size="sm" c="dimmed">
              This determines the set of products which will be part of the trigger.
            </Text>
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={{ base: 12, sm: 3 }}>Audience Rules</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 9 }}>
            <Rules
              rules={catalogTrigger.audienceRules}
              campaigns={campaigns}
              webhooks={webhooks}
              emails={emails}
              updateRules={(audienceRules) => setCatalogTrigger({ ...catalogTrigger, audienceRules })}
              availableCustomFields={customFields}
              eventTags={eventTags}
              eventActions={eventActions}
              subscriptions={subscriptions}
              pushNotifications={pushNotifications}
              textMessages={textMessages}
              viberMessages={viberMessages}
              pages={pages}
              availableRules={availableBehavioralRules}
              availableFilters={behavioralRuleFilters}
              segments={segments}
              whatsappMessages={whatsappMessages}
              surveys={surveys}
            />
            <Text size="sm" c="dimmed">
              This determines the set of profiles which will receive the trigger. For example, if you wish to target
              profiles who have viewed a product which is now in stock, you need to select 'In stock' under Catalog
              Rules and 'Viewed Product' under Audience Rules and Releva will automatically select the profiles which
              have viewed at least one of the products which have recently become in stock.{' '}
            </Text>
          </Grid.Col>
        </Grid>

        <Group justify="flex-end" gap="xs" py="md">
          <Button variant="default" onClick={() => navigate(-1)}>
            {' '}
            Back
          </Button>
          {deleteButton}
          {duplicateButton}
          <LockableButton
            onClick={() => saveCatalogTrigger(catalogTrigger)}
            restrictionKey={`catalogTriggers${catalogTrigger.id ? 'Update' : 'Create'}`}
            disabled={saveDisabled()}
            data-tour="catalogTriggerSaveBtn"
          >
            <IconDeviceFloppy size={14} /> Save
          </LockableButton>
          <ConfirmationModal
            error={deleteError}
            show={confirmDeleteOpen}
            close={() => setConfirmDeleteOpen(false)}
            confirm={(cb) => {
              setDeleteError(undefined);
              deleteCatalogTrigger(cb).catch(setDeleteError);
            }}
            message="Deleting a catalog trigger will stop the propagation of events for this trigger. Any campaigns or workflows using this trigger will no longer be triggered. Are you sure?"
          />
        </Group>
      </Box>
    </Grid.Col>
  );
};

export default CatalogTriggerForm;
